import { useFeatureValue } from "@growthbook/growthbook-react";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useRouter } from "next/router";
import { BiInfoCircle } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { MAX_BOOKING_MONTHS_IN_ADVANCE } from "../../../../../core/availability";
import {
  Product,
  ProductAvailabilityType,
} from "../../../../../graphql/operations";
import useAvailability from "../../../../../hooks/useAvailability";
import { formatDate } from "../../../../../utils/calendarUtils";
import Button from "../../../../Atoms/Button";
import AddToCartButton from "../../../../Cards/Cart/AddToCartButton";
import type { AddToCartProduct } from "../../../../Cards/Cart/AddToCartButton/Form/Form";
import HurryUpMessage from "../../../../Cards/HurryUpMessage";
import ReservationDatepicker, {
  ReservationDatepickerProps,
} from "../../../../Cards/ReservationDatepicker";
import HowThisWorks from "../../../Checkout/Parts/HowThisWorks";
import WaitingListButton from "../ProductWaitingListButton";
import { CALENDAR_AVAILABILITY_CACHE } from "../../helpers";
import styles from "./ProductCalendar.module.scss";

export type ProductCalendarProps = {
  countryName: string;
  productId: string;
  productName?: string;
  faqs?: AddToCartProduct["faqs"];
  location?: { countryId?: string | null } | null;
  children: ReactNode;
  inlineCalendar?: boolean;
  productOptions?: any;
  setIsAvailable: Function;
  initalAvailability: ReturnType<typeof useAvailability>;
} & Pick<ReservationDatepickerProps, "isPrivate" | "toggleIsPrivate"> &
  Pick<Product, "availabilityType" | "privatePrices">;

const BOOKING_SIZE = 1;

export default function ProductCalendar({
  productId,
  productName,
  faqs,
  location,
  children,
  inlineCalendar,
  productOptions,
  isPrivate,
  toggleIsPrivate,
  availabilityType,
  initalAvailability,
  setIsAvailable,
  privatePrices,
  countryName,
}: ProductCalendarProps) {
  // A: Book Now, B: both, C: Add To Cart
  const bookingButtons = useFeatureValue("product-page-booking-buttons", "A");
  const showBookNow = bookingButtons === "A" || bookingButtons === "B";
  const showAddToCart = bookingButtons !== "A";
  const router = useRouter();
  const { query } = router;
  const queryDate = query?.date as unknown as Date;

  const availability = useAvailability({
    productId: productId,
    isPrivate,
    duration: { months: MAX_BOOKING_MONTHS_IN_ADVANCE },
    preloadedAvailability: initalAvailability.availabilities,
    cache: CALENDAR_AVAILABILITY_CACHE,
  });
  const [date, setDate] = useState<Date | null>(queryDate);
  const [time, setTime] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const {
    error,
    isAvailableInYear,
    isAvailableInRange,
    availableDates,
    isTimeSlotAvailable,
    getAvailableTimeSlots,
  } = availability;

  useEffect(() => {
    const selectedDate = date ?? availableDates[0]?.date;
    if (!selectedDate) return;

    if (!date) setDate(selectedDate);
    if (time && isTimeSlotAvailable(selectedDate, time).available) return;

    const nextTime = getAvailableTimeSlots(selectedDate)[0]?.time ?? null;
    if (time !== nextTime) setTime(nextTime);
  }, [date, time, availableDates, isTimeSlotAvailable, getAvailableTimeSlots]);

  useEffect(() => {
    setIsAvailable(isAvailableInYear);
  }, [isAvailableInYear, setIsAvailable]);

  const [openDis, setOpenDis] = useState(false);
  const onOpenDisModal = () => setOpenDis(true);
  const onCloseDisModal = () => setOpenDis(false);
  const modalRef = useRef(null);

  const UnavailableTourMessageContainer = useCallback(
    ({ children }: React.PropsWithChildren) => {
      return productId && productOptions ? (
        <WaitingListButton
          productId={productId}
          productOptions={productOptions}
        >
          {children}
        </WaitingListButton>
      ) : (
        <p style={{ textAlign: "center" }}>{children}</p>
      );
    },
    [productId, productOptions],
  );

  if (error) return <p>{error.message}</p>;

  return (
    <div className={styles.Container}>
      {isAvailableInYear ? (
        <>
          <HurryUpMessage isAvailableInRange={isAvailableInRange} />
          <ReservationDatepicker
            countryName={countryName}
            {...availability}
            isPrivate={isPrivate}
            toggleIsPrivate={toggleIsPrivate}
            inlineCalendar={inlineCalendar}
            date={date}
            time={time}
            setDate={setDate}
            setTime={setTime}
            bookingSize={BOOKING_SIZE}
            allowUnavailableDateClick={true}
            sourceProductId={productId}
            canToggleGroupPrivacy={
              availabilityType === ProductAvailabilityType.PublicAndPrivate &&
              !!privatePrices.find(
                (privatePrice) => privatePrice.price !== null,
              )
            }
          />
          {children && <div className={styles.box}>{children}</div>}
          {showAddToCart && (
            <AddToCartButton
              showDiscount={showBookNow}
              style={{ marginBottom: showBookNow ? "16px" : "5px" }}
              productId={productId}
              product={{ name: productName, location, faqs }}
              date={formatDate(date)}
              time={time}
            />
          )}
          {showBookNow && (
            <Button
              large
              disabled={isRedirecting}
              style={{ width: "100%", marginBottom: "5px" }}
              onClick={async () => {
                setIsRedirecting(true);
                const checkoutQuery = { ...router.query };
                delete checkoutQuery.id;
                try {
                  await router.push({
                    pathname: `/checkout/${productId}`,
                    query: {
                      ...checkoutQuery,
                      date: formatDate(date),
                      time,
                      isprivate: isPrivate ? "true" : "",
                    },
                  });
                } finally {
                  setIsRedirecting(false);
                }
              }}
            >
              {isRedirecting ? (
                <TailSpin
                  color="#FFFFFF"
                  height={18}
                  width={18}
                  wrapperStyle={{ justifyContent: "center", alignItems: "center" }}
                  ariaLabel="Opening checkout"
                />
              ) : (
                "Book Now"
              )}
            </Button>
          )}
          <div className={styles.HowThisWorksLink} onClick={onOpenDisModal}>
            <BiInfoCircle
              color="#D81B60"
              size={16}
              style={{ position: "relative", top: 3 }}
            />{" "}
            <span
              style={{
                color: "#D81B60",
                textDecoration: "underline",
                fontSize: 14,
              }}
            >
              How it works
            </span>
          </div>
          {productId && productOptions && (
            <>
              <WaitingListButton
                productId={productId}
                productOptions={productOptions}
              />
            </>
          )}
        </>
      ) : (
        <UnavailableTourMessageContainer>
          Currently not available
        </UnavailableTourMessageContainer>
      )}
      <Modal
        open={openDis}
        blockScroll={false}
        onClose={onCloseDisModal}
        ref={modalRef}
        initialFocusRef={modalRef}
        classNames={{
          modal: styles.CustomModal2,
        }}
      >
        <HowThisWorks />
      </Modal>
    </div>
  );
}
